import type { ArcadeInputBindings, ArcadeInputHandlers } from './types';
import {
  isAmmoHudTarget,
  isChaffHudTarget,
  isDesktopStartIgnoredTarget,
  isRailgunHudTarget,
  isTouchEndUiTarget,
  isTouchPlayIgnoredTarget,
  isWeaponCycleIgnoredTarget,
} from './interactiveTargets';

/**
 * Keyboard, pointer, and touch bindings for arcade + desktop start.
 * Behavior matches the previous App-local handlers (no product changes).
 */
export function bindArcadeInput(handlers: ArcadeInputHandlers): ArcadeInputBindings {
  const {
    getArcade,
    getShellState,
    beginResumeCountdown,
    tryStartArcade,
    dismissGameOver,
    hasOpenWindows,
    getLastTouchEndAt,
    setLastTouchEndAt,
    getMobileTouchStartAt,
    setMobileTouchStartAt,
  } = handlers;

  const onKeyDown = (event: KeyboardEvent) => {
    const arcade = getArcade();
    if (!arcade) return;

    if (event.code === 'Space') {
      // Avoid scrolling the page / toggling focused controls
      event.preventDefault();
      if (arcade.isGameOver()) {
        if (!event.repeat) {
          dismissGameOver();
        }
        return;
      }
      if (arcade.isPlaying()) {
        const { showSettings, pauseUi } = getShellState();
        // Pause / settings: Space resumes via countdown, never fires
        if (showSettings || pauseUi === 'settings') {
          return;
        }
        if (pauseUi === 'countdown') {
          return;
        }
        if (pauseUi === 'prompt') {
          if (!event.repeat) {
            beginResumeCountdown();
          }
          return;
        }
        // Desktop: hold Space for continuous railgun fire; first press also shoots
        arcade.setFireHeld(true);
        if (!event.repeat) {
          arcade.tryFire();
        }
        return;
      }
      if (!event.repeat) {
        tryStartArcade();
      }
      return;
    }
    const { showSettings, pauseUi } = getShellState();
    if (arcade.isPlaying() && (arcade.isPaused() || pauseUi !== 'none' || showSettings)) {
      // Block combat inputs while paused (settings / prompt / countdown)
      return;
    }
    if (event.code === 'KeyX' && arcade.isPlaying()) {
      event.preventDefault();
      // Ignore key-repeat so hold after lock doesn’t immediately unlock
      if (!event.repeat) {
        arcade.setLockHold(true);
      }
      return;
    }
    if (event.code === 'KeyV' && arcade.isPlaying()) {
      event.preventDefault();
      // Soft-lock acquire still blocks flip; hard lock can clear via railgun switch
      arcade.cycleWeaponSafe();
      return;
    }
    if (event.code === 'KeyF' && arcade.isPlaying()) {
      event.preventDefault();
      arcade.tryFire();
      return;
    }
    if (arcade.isPlaying()) {
      arcade.setKey(event.code, true);
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const arcade = getArcade();
    if (!arcade) return;
    if (event.code === 'Space') {
      arcade.setFireHeld(false);
    }
    if (event.code === 'KeyX') {
      arcade.setLockHold(false);
    }
    if (arcade.isPlaying()) {
      arcade.setKey(event.code, false);
    }
  };

  const onPointerDown = (event: MouseEvent) => {
    const arcade = getArcade();
    if (!arcade || !arcade.isPlaying()) {
      return;
    }
    const { showSettings, pauseUi } = getShellState();
    if (arcade.isPaused() || pauseUi !== 'none' || showSettings) {
      return;
    }
    // Synthetic click after touch / lock gesture must not flip weapons
    if (performance.now() - getLastTouchEndAt() < 700) {
      return;
    }
    if (event.button !== 0) {
      return;
    }
    if (arcade.isLockHolding()) {
      return;
    }
    if (isWeaponCycleIgnoredTarget(event.target)) {
      return;
    }
    arcade.cycleWeaponSafe();
  };

  const onTouchStart = (event: TouchEvent) => {
    const arcade = getArcade();
    if (!arcade) return;

    if (arcade.isGameOver()) {
      dismissGameOver();
      return;
    }
    if (arcade.isPlaying()) {
      const { showSettings, pauseUi } = getShellState();
      if (arcade.isPaused() || pauseUi !== 'none' || showSettings) {
        return;
      }
      const target = event.target;
      if (isAmmoHudTarget(target)) {
        arcade.setWeapon('missile');
        return;
      }
      if (isRailgunHudTarget(target)) {
        arcade.setWeapon('railgun');
        return;
      }
      if (isChaffHudTarget(target)) {
        arcade.setWeapon('chaff');
        return;
      }
      if (isTouchPlayIgnoredTarget(target)) {
        return;
      }
      // Long-press anywhere → enemy lock; short tap on release → fire
      setMobileTouchStartAt(performance.now());
      arcade.setLockHold(true);
      return;
    }
    if (hasOpenWindows()) {
      return;
    }
    if (isDesktopStartIgnoredTarget(event.target)) {
      return;
    }
    tryStartArcade();
  };

  const onTouchEnd = (event: TouchEvent) => {
    const arcade = getArcade();
    if (!arcade || !arcade.isPlaying()) {
      return;
    }
    if (isTouchEndUiTarget(event.target)) {
      arcade.setLockHold(false);
      return;
    }
    const heldMs = performance.now() - getMobileTouchStartAt();
    arcade.setLockHold(false);
    setLastTouchEndAt(performance.now());
    // Short tap = fire; long-press was for lock (handled in-game)
    if (heldMs < 350) {
      arcade.tryFire();
    }
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('mousedown', onPointerDown);
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchend', onTouchEnd, { passive: true });

  const dispose = () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('mousedown', onPointerDown);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchend', onTouchEnd);
  };

  return {
    onKeyDown,
    onKeyUp,
    onPointerDown,
    onTouchStart,
    onTouchEnd,
    dispose,
  };
}
